<template>
  <div class="user-account-popup-root">
    <transition name="account-fade">
      <div v-if="visible" class="account-overlay" @click.self="close">
        <div class="account-popup" role="dialog" aria-modal="true" @click.stop>
          <button class="account-close" type="button" aria-label="关闭" @click="close">
            <i class="el-icon-close"></i>
          </button>

          <div class="account-avatar-wrap">
            <div class="account-avatar-ring">
              <img :src="avatar" alt="avatar" class="account-avatar" />
            </div>
            <button
              class="account-camera-btn"
              type="button"
              title="更换照片"
              :disabled="avatarUploading"
              @click="uploadAvatar"
            >
              <i :class="avatarUploading ? 'el-icon-loading' : 'el-icon-camera'"></i>
            </button>
            <input
              ref="avatarInput"
              type="file"
              accept="image/jpeg,image/png"
              class="account-avatar-input"
              @change="onAvatarFileChange"
            >
          </div>

          <div class="account-greeting">{{ displayName }}，您好！</div>

          <div class="account-actions">
            <button class="account-action-btn" type="button" @click="handleLogout">
              <i class="fa fa-sign-out"></i>
              <span>退出账号</span>
            </button>
          </div>

          <div class="account-footer">
            <a href="javascript:;" @click.prevent="openPrivacy">隐私权政策</a>
            <span class="dot">·</span>
            <a href="javascript:;" @click.prevent="openTerms">服务条款</a>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { axios } from '@/config/axios'

export default {
  name: 'UserAccountPopup',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    user: {
      type: Object,
      default: () => ({})
    },
    defaultAvatar: {
      type: String,
      default: '/img/def_img.png'
    }
  },
  data() {
    return {
      localAvatar: '',
      avatarUploading: false
    }
  },
  computed: {
    displayName() {
      return this.user.username || '用户'
    },
    avatar() {
      return this.localAvatar || this.user.avatar || this.defaultAvatar
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.localAvatar = ''
      }
    }
  },
  methods: {
    close() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    uploadAvatar() {
      if (this.avatarUploading) return
      this.$refs.avatarInput && this.$refs.avatarInput.click()
    },
    onAvatarFileChange(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return

      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png'
      const isLt2M = file.size / 1024 / 1024 < 2
      if (!isJPG) {
        this.$message.error('头像图片只能是 JPG/PNG 格式!')
        return
      }
      if (!isLt2M) {
        this.$message.error('头像图片大小不能超过 2MB!')
        return
      }

      this.uploadAvatarFile(file)
    },
    async uploadAvatarFile(file) {
      this.avatarUploading = true
      try {
        const formData = new FormData()
        formData.append('file', file)

        const response = await axios({
          url: 'appletUser/update-img',
          method: 'post',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          // 避免全局 qs.stringify 破坏 FormData
          transformRequest: [(data) => data]
        })

        if (response.data.code !== 200 || !response.data.data) {
          this.$message.error(response.data.message || '头像上传失败')
          return
        }

        const avatarUrl = response.data.data
        this.localAvatar = avatarUrl
        try {
          const raw = localStorage.getItem('userInfo')
          if (raw) {
            const parsed = JSON.parse(raw)
            parsed.avatar = avatarUrl
            if (parsed.studentProfile) {
              parsed.studentProfile.avatarUrl = avatarUrl
            }
            localStorage.setItem('userInfo', JSON.stringify(parsed))
          }
        } catch (err) {
          // ignore persist errors
        }
        this.$emit('avatar-updated', avatarUrl)
        this.$message.success('头像更新成功')
      } catch (error) {
        console.error('头像上传失败:', error)
        this.$message.error('头像上传失败，请重试')
      } finally {
        this.avatarUploading = false
      }
    },
    handleLogout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const uuid = localStorage.getItem('pcUuid')
          if (uuid) {
            await axios.get('/student/loginOff', {
              params: { uuid }
            })
          }
        } catch (error) {
          console.error('退出登录接口调用失败:', error)
        }
        localStorage.removeItem('userInfo')
        localStorage.removeItem('token')
        this.close()
        this.$message.success('已退出登录')
        this.$router.push('/login')
      }).catch(() => {})
    },
    openPrivacy() {
      this.$message.info('隐私权政策即将上线')
    },
    openTerms() {
      this.$message.info('服务条款即将上线')
    }
  }
}
</script>

<style lang="less" scoped>
.account-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: transparent;
}

.account-popup {
  position: absolute;
  left: 12px;
  bottom: 72px;
  width: 320px;
  max-width: calc(100vw - 24px);
  background: #2d2e30;
  border-radius: 28px;
  padding: 28px 28px 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
  text-align: center;
  color: #e3e3e3;
}

.account-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #e3e3e3;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
}

.account-avatar-wrap {
  position: relative;
  width: 96px;
  height: 96px;
  margin: 22px auto 0;
}

.account-avatar-ring {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  padding: 3px;
  background: conic-gradient(
    #4285f4 0deg 90deg,
    #ea4335 90deg 180deg,
    #fbbc05 180deg 270deg,
    #34a853 270deg 360deg
  );
  box-sizing: border-box;
}

.account-avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  border: 3px solid #2d2e30;
  box-sizing: border-box;
  background: #3c4043;
}

.account-avatar-input {
  display: none;
}

.account-camera-btn {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid #2d2e30;
  background: #3c4043;
  color: #e3e3e3;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: #4a4d51;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}

.account-greeting {
  margin-top: 18px;
  font-size: 28px;
  font-weight: 400;
  color: #fff;
  line-height: 1.3;
}

.account-actions {
  display: flex;
  margin-top: 28px;
}

.account-action-btn {
  flex: 1;
  height: 48px;
  border-radius: 24px;
  border: 1px solid #5f6368;
  background: transparent;
  color: #e3e3e3;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.15s ease;

  i {
    font-size: 16px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
}

.account-footer {
  margin-top: 22px;
  font-size: 12px;
  color: #9aa0a6;

  a {
    color: #9aa0a6;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .dot {
    margin: 0 6px;
  }
}

.account-fade-enter-active,
.account-fade-leave-active {
  transition: opacity 0.18s ease;
}

.account-fade-enter,
.account-fade-leave-to {
  opacity: 0;
}
</style>
